import { COLLECTION_NOUN } from '@platform/schema/content';

import { HOME } from './collection';

import type { ReadinessIssue } from '@platform/schema/authoring';

/** The columns a collection page shows a field for, by the label it shows. */
const ON_PAGE = {
  title: 'Title',
  summary: 'Summary',
  subject: 'Topic',
  difficulty: 'Level',
  objectName: 'Card illustration',
  parentId: 'Course',
} as const;

type Column = keyof typeof ON_PAGE;

const onPage = (path: string): path is Column => path in ON_PAGE;

/** A settings field's id and label, so the form and the jump to it name it alike. */
export const field = (column: Column) => ({
  id: `field-${column}`,
  label: ON_PAGE[column],
});

const PAGE = { ...HOME, LESSON: '/authoring/lessons' };

const NOUN = { ...COLLECTION_NOUN, LESSON: 'lesson' };

const SCENE = /^scenes\[(\d+)\]/;

export interface RefusalGroup {
  key: string;
  heading: string;
  /** Where to fix it. Absent for the page the panel is on. */
  href: string | undefined;
  rules: {
    message: string;
    field: { id: string; label: string } | undefined;
    where: string | undefined;
  }[];
}

/**
 * A subtree's refusals, one group per card — GEN-108.
 *
 * Pooled, the module's missing art and a lesson's read the same, so an admin could not
 * tell which card to open. A rule on this page points at its field; one elsewhere links
 * to the page that has it.
 */
export function refusalGroups(
  issues: readonly ReadinessIssue[],
  ownId: string,
): RefusalGroup[] {
  const groups = new Map<string, RefusalGroup>();

  for (const { path, message, item } of issues) {
    const own = item.id === ownId;
    let group = groups.get(item.id);

    if (group === undefined) {
      group = {
        key: item.id,
        heading: own
          ? `This ${NOUN[item.kind]}`
          : `${capital(NOUN[item.kind])}: ${item.title}`,
        href: own ? undefined : `${PAGE[item.kind]}/${item.id}`,
        rules: [],
      };
      groups.set(item.id, group);
    }

    const scene = SCENE.exec(path)?.[1];

    group.rules.push({
      message,
      field: own && onPage(path) ? field(path) : undefined,
      where: scene === undefined ? undefined : `Scene ${scene}`,
    });
  }

  return [...groups.values()];
}

const capital = (word: string): string =>
  word.charAt(0).toUpperCase() + word.slice(1);
